import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { pagedSessionsSchema } from '../schemas/sessionSchemas';
import { sessionKeys } from './sessionKeys';

interface ListSessionsParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  workflowId?: string;
  /**
   * Whether emailed-but-never-opened invites may appear as Not Started rows. They only
   * ever appear when Not Started is among the requested statuses; this opts out of them
   * even then. The sessions table wants them; the detail view's navigator does not,
   * because its rows open a session and an invite has none. Defaults to true.
   */
  includeInvites?: boolean;
  /** Skip the request entirely. */
  enabled?: boolean;
}

export function useListSessions({
  page,
  pageSize,
  search,
  status,
  workflowId,
  includeInvites = true,
  enabled = true,
}: ListSessionsParams) {
  const tenantId = useTenantStore((s) => s.tenantId);

  return useQuery({
    queryKey: sessionKeys.list({ page, pageSize, search, status, workflowId, includeInvites }),
    queryFn: async ({ signal }) => {
      const searchParams: Record<string, string | number | boolean> = { page, pageSize };
      if (search) searchParams.search = search;
      if (status) searchParams.status = status;
      if (workflowId) searchParams.workflowId = workflowId;
      if (!includeInvites) searchParams.includeInvites = false;

      const raw = await apiClient
        .get(`api/v1/t/${tenantId}/sessions`, { searchParams, signal })
        .json();

      return pagedSessionsSchema.parse(raw);
    },
    enabled: enabled && !!tenantId,
  });
}
